import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiHome, FiDownload, FiAlertCircle } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import chargilyService from '../services/chargilyService';
import supabaseService from '../services/supabaseService';

const PaymentSuccessScreen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [isLocalhost, setIsLocalhost] = useState(false);

  useEffect(() => {
    const localhost = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
    setIsLocalhost(localhost);
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    try {
      // الحصول على checkout_id من URL
      let checkoutId = searchParams.get('session_id');
      
      // 🔧 في حالة localhost، Chargily لن يستطيع إعادة التوجيه الحقيقي
      const isLocal = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
      
      if (isLocal) {
        console.warn('⚠️ Running in localhost mode');
        
        // إذا لم يكن هناك checkout_id، استخدم محاكاة
        if (!checkoutId) {
          console.log('💡 Using mock payment for localhost testing');
          const mockData = {
            amount: parseFloat(localStorage.getItem('mock_payment_amount') || '1000'),
            currency: 'DZD',
            transactionId: 'MOCK_' + Date.now(),
            paidAt: new Date().toISOString(),
            description: 'دفع تجريبي - وضع التطوير المحلي'
          };
          setPaymentData(mockData);
          toast.success('تم الدفع بنجاح (وضع التطوير)');
          localStorage.removeItem('mock_payment_amount');
          setLoading(false);
          return;
        }
      }
      
      if (!checkoutId) {
        throw new Error('معرف العملية غير موجود');
      }

      console.log('🔍 Verifying payment for checkout:', checkoutId);

      // التحقق من حالة الدفع من Chargily
      const result = await chargilyService.getCheckoutStatus(checkoutId);

      if (!result.success) {
        throw new Error(result.error || 'فشل التحقق من حالة الدفع');
      }

      const checkout = result.data;
      console.log('✅ Payment verified:', checkout);

      // التحقق من أن الدفع مكتمل
      if (checkout.status !== 'paid') {
        throw new Error('الدفع لم يكتمل بعد');
      }

      // حفظ بيانات الدفع في قاعدة البيانات
      if (checkout.metadata?.provider_id) {
        const now = new Date();
        const currentMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));

        await supabaseService.client
          .from('monthlypayments')
          .upsert({
            provider_id: checkout.metadata.provider_id,
            month: currentMonth.toISOString(),
            payment_status: 'paid',
            receipt_url: checkout.invoice_url || null,
            amount: checkout.amount / 100, // تحويل من سنتيم
            payment_method: 'chargily',
            transaction_id: checkout.id,
            paid_at: checkout.paid_at || new Date().toISOString(),
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'provider_id,month'
          });
      }

      // إرسال إشعار للمستخدم
      if (checkout.metadata?.user_id) {
        await supabaseService.createNotification({
          user_id: checkout.metadata.user_id,
          title: 'تم الدفع بنجاح ✅',
          message: `تمت عملية الدفع بنجاح بمبلغ ${checkout.amount / 100} دج عبر Chargily`,
          is_read: false
        });
      }

      // حفظ بيانات الدفع في الحالة
      setPaymentData({
        amount: checkout.amount / 100,
        currency: 'DZD',
        transactionId: checkout.id,
        paidAt: checkout.paid_at,
        invoiceUrl: checkout.invoice_url,
        description: checkout.description
      });

      // تنظيف localStorage
      localStorage.removeItem('pending_payment');

      toast.success('تم الدفع بنجاح!');
      
    } catch (error) {
      console.error('❌ Error verifying payment:', error);
      setError(error.message);
      toast.error(error.message || 'فشل التحقق من الدفع');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    if (paymentData?.invoiceUrl) {
      window.open(paymentData.invoiceUrl, '_blank');
    } else {
      toast.error('الفاتورة غير متوفرة');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700 font-semibold">جاري التحقق من الدفع...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">فشل التحقق من الدفع</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/provider')}
            className="w-full bg-khidma-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        
        {/* تحذير وضع التطوير */}
        {isLocalhost && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-start space-x-2 space-x-reverse">
            <FiAlertCircle className="text-yellow-600 text-lg mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-800">
              أنت في وضع التطوير المحلي (localhost). هذا دفع تجريبي فقط.
            </p>
          </div>
        )}

        {/* أيقونة النجاح */}
        <div className="text-center mb-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <FiCheckCircle className="w-14 h-14 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">تم الدفع بنجاح!</h1>
          <p className="text-gray-600">
            {isLocalhost ? 'تم محاكاة عملية الدفع بنجاح' : 'تمت عملية الدفع بنجاح عبر Chargily'}
          </p>
        </div>

        {/* تفاصيل الدفع */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6 space-y-4">
          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
            <span className="text-gray-600">المبلغ المدفوع</span>
            <span className="text-2xl font-bold text-green-600">
              {paymentData?.amount?.toLocaleString('ar-DZ')} دج
            </span>
          </div>
          
          <div className="flex justify-between items-center border-b border-gray-200 pb-3">
            <span className="text-gray-600">رقم العملية</span>
            <span className="text-sm font-mono text-gray-800 break-all">
              {paymentData?.transactionId?.substring(0, 20)}...
            </span>
          </div>
          
          {paymentData?.paidAt && (
            <div className="flex justify-between items-center">
              <span className="text-gray-600">تاريخ الدفع</span>
              <span className="text-sm text-gray-800">
                {new Date(paymentData.paidAt).toLocaleDateString('ar-DZ', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          )}
        </div>

        {/* الأزرار */}
        <div className="space-y-3">
          {paymentData?.invoiceUrl && !isLocalhost && (
            <button
              onClick={handleDownloadInvoice}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 space-x-reverse"
            >
              <FiDownload className="text-xl" />
              <span>تحميل الفاتورة</span>
            </button>
          )}
          
          <button
            onClick={() => navigate('/provider')}
            className="w-full bg-khidma-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 space-x-reverse"
          >
            <FiHome className="text-xl" />
            <span>العودة للرئيسية</span>
          </button>
        </div>

        {/* معلومات إضافية */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {isLocalhost 
              ? 'هذا دفع تجريبي في وضع التطوير المحلي' 
              : 'تم الدفع بأمان عبر بوابة Chargily'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessScreen;